/**
 * The programme this tab opened to view rather than edit.
 *
 * Kept in session storage so a reload stays in View, and per tab so another
 * tab editing the same programme is not switched with it.
 *
 * @module components/shell/useViewOnlyProgramme
 */

import { useCallback, useState } from "react";

const STORAGE_KEY = "socratiq:view-only-programme";

function stored(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Whether the open programme was opened to view, and the way to choose. */
export function useViewOnlyProgramme(openProgrammeId: string | undefined): {
  viewingProgramme: boolean;
  setProgrammeViewMode: (programmeId: string, viewing: boolean) => void;
} {
  const [viewOnlyProgrammeId, setViewOnlyProgrammeId] = useState<string | null>(stored);
  const setProgrammeViewMode = useCallback((programmeId: string, viewing: boolean): void => {
    const next = viewing ? programmeId : null;
    setViewOnlyProgrammeId(next);
    try {
      if (next) {
        window.sessionStorage.setItem(STORAGE_KEY, next);
      } else {
        window.sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage refused: View still holds for this page's life.
    }
  }, []);
  return {
    viewingProgramme: openProgrammeId !== undefined && viewOnlyProgrammeId === openProgrammeId,
    setProgrammeViewMode,
  };
}
